import type { EventBus } from '../core/EventBus';
import type { Store } from '../core/Store';

const VOLUME_STEP = 0.1;
const SEEK_STEP = 5;
const SEEK_STEP_SHIFT = 1;

export class Hotkeys {
  constructor(
    private eventBus: EventBus,
    private store: Store,
    private playerRoot: HTMLElement,
  ) {
    this.playerRoot.addEventListener('keydown', this.onKeyDown);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;

    let handled = true;

    switch (e.code) {
      case 'Space':
      case 'KeyK': {
        const state = this.store.getState();
        if (state.isPlaying) {
          this.eventBus.emit('pause');
        } else {
          this.eventBus.emit('play');
        }
        break;
      }

      case 'ArrowUp': {
        const vol = Math.round(Math.min(1, this.store.getState().volume + VOLUME_STEP) * 100) / 100;
        this.eventBus.emit('volume:change', vol);
        break;
      }

      case 'ArrowDown': {
        const vol = Math.round(Math.max(0, this.store.getState().volume - VOLUME_STEP) * 100) / 100;
        this.eventBus.emit('volume:change', vol);
        break;
      }

      case 'ArrowRight': {
        const step = e.shiftKey ? SEEK_STEP_SHIFT : SEEK_STEP;
        this.eventBus.emit('time:update', step);
        break;
      }

      case 'ArrowLeft': {
        const step = e.shiftKey ? -SEEK_STEP_SHIFT : -SEEK_STEP;
        this.eventBus.emit('time:update', step);
        break;
      }

      case 'KeyF': {
        if (e.ctrlKey || e.altKey || e.metaKey) {
          handled = false;
          break;
        }
        this.eventBus.emit('fullscreen:change', !this.store.getState().isFullscreen);
        break;
      }

      case 'KeyM': {
        this.eventBus.emit('muted:change', !this.store.getState().isMuted);
        break;
      }

      case 'KeyB': {
        if (!e.altKey) { handled = false; break; }
        if (this.store.getState().downloadAvailable) {
          this.eventBus.emit('download:trigger');
        }
        break;
      }

      case 'KeyX': {
        if (!e.altKey) { handled = false; break; }
        const state = this.store.getState();
        if (state.clipsAvailable && state.isPlaying) {
          this.eventBus.emit('clips:create');
        }
        break;
      }

      case 'KeyI': {
        if (e.ctrlKey || e.altKey || e.metaKey) { handled = false; break; }
        this.eventBus.emit('player-in-window:open');
        break;
      }

      case 'Backquote': {
        if (e.ctrlKey || e.altKey || e.metaKey) { handled = false; break; }
        this.eventBus.emit('debug:toggle');
        break;
      }

      default:
        handled = false;
    }

    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  destroy(): void {
    this.playerRoot.removeEventListener('keydown', this.onKeyDown);
  }
}
